import { useLayoutEffect, useRef } from "react";

const stack: HTMLElement[] = [];
let originalOverflow = "";
let rootWasInert = false;
let lastInteraction: HTMLElement | null = null;
/** Capture the actual activator even when a browser does not focus clicked buttons. */
export function rememberDialogTrigger(event: Event) {
  const element = event.target instanceof Element ? event.target.closest<HTMLElement>('button,input,textarea,select,summary,a[href],[tabindex],[role="button"],[role="menuitem"]') : null;
  if (!element || element.closest('[inert]')) return;
  const menu = element.closest<HTMLElement>('[role="menu"]');
  const trigger = menu?.id ? document.querySelector<HTMLElement>(`[aria-controls="${CSS.escape(menu.id)}"]`) : null;
  lastInteraction = trigger ?? element;
}
const focusable = 'button:not(:disabled), [href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), summary, [tabindex]:not([tabindex="-1"])';

/** One keyboard owner and one scroll lock, including nested confirmations. */
export function useDialogLifecycle(open: boolean, onClose: () => void, busy = false) {
  const ref = useRef<HTMLElement | null>(null);
  const latest = useRef({ onClose, busy });
  latest.current = { onClose, busy };
  useLayoutEffect(() => {
    const node = ref.current;
    if (!open || !node) return;
    const previousFocus = lastInteraction?.isConnected && !node.contains(lastInteraction) ? lastInteraction : document.activeElement as HTMLElement | null;
    const root = document.getElementById("root");
    if (!stack.length) {
      originalOverflow = document.body.style.overflow;
      rootWasInert = root?.inert ?? false;
      document.body.style.overflow = "hidden";
      if (root && !root.contains(node)) root.inert = true;
    }
    stack.push(node);
    const layer = node.closest<HTMLElement>(".modal-backdrop") ?? node;
    layer.style.zIndex = String(1100 + stack.length * 20);
    const items = () => [...node.querySelectorAll<HTMLElement>(focusable)].filter((item) => item.tabIndex >= 0 && item.getClientRects().length && item.checkVisibility?.() !== false && !item.matches(':disabled') && !item.closest('[inert], [hidden], [aria-hidden="true"]') && getComputedStyle(item).visibility !== "hidden");
    // Focus before the dialog becomes interactive: a delayed frame could steal
    // focus between a user's first pointer-down and text input.
    const initial = node.querySelector<HTMLElement>('[data-autofocus], [autofocus]');
    (initial && !initial.matches(':disabled') && !initial.closest('[hidden], [inert]') ? initial : items()[0] ?? node).focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (stack.at(-1) !== node || event.isComposing || event.keyCode === 229) return;
      if (event.key === "Escape") {
        event.preventDefault(); event.stopImmediatePropagation();
        if (!latest.current.busy) latest.current.onClose();
      } else if (event.key === "Tab" && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // Own the complete cycle, including engines that omit native buttons
        // from ordinary Tab navigation. Hidden/disabled controls stay excluded.
        const options = items();
        event.preventDefault();
        if (!options.length) { node.focus(); return; }
        const current = options.indexOf(document.activeElement as HTMLElement);
        const next = current < 0 ? (event.shiftKey ? options.length - 1 : 0)
          : (current + (event.shiftKey ? -1 : 1) + options.length) % options.length;
        options[next]?.focus();
      }
    };
    const onFocus = (event: FocusEvent) => {
      if (stack.at(-1) === node && event.target instanceof Node && !node.contains(event.target)) (items()[0] ?? node).focus({ preventScroll: true });
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("focusin", onFocus);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("focusin", onFocus);
      const wasTop = stack.at(-1) === node;
      const index = stack.indexOf(node); if (index >= 0) stack.splice(index, 1);
      if (!stack.length) { document.body.style.overflow = originalOverflow; if (root) root.inert = rootWasInert; }
      if (wasTop && previousFocus?.isConnected) {
        const restore = () => {
          const top = stack.at(-1);
          if (!previousFocus.isConnected || previousFocus.closest('[inert]') || (top && !top.contains(previousFocus))) return;
          previousFocus.focus({ preventScroll: true });
        };
        restore();
        // Some engines finish applying inert removal only after this commit.
        queueMicrotask(() => { if (document.activeElement === document.body || document.activeElement === node || node.contains(document.activeElement)) restore(); });
      }
    };
  }, [open]);
  return ref;
}
